import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox,
  Check,
  X,
  Sparkles,
  FileText,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useProposedEvidence,
  useBatchConfirmEvidence,
  useBatchDismissEvidence,
  useScanEvidence,
  type EvidenceScanResult,
} from '@/hooks/use-proposed-evidence';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { ProposedEvidenceItem } from '@/lib/types';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null, timeZone: string): string {
  if (!iso) return '—';
  return formatDateTimeInTimeZone(iso, timeZone, { dateStyle: 'medium' });
}

function sourceLabel(item: ProposedEvidenceItem): string {
  if (!item.source_module || !item.source_model) return '—';
  const model = item.source_model.charAt(0).toUpperCase() + item.source_model.slice(1);
  return `${model} #${item.source_object_id ?? '?'}`;
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function ProposedEvidencePage() {
  const timeZone = useOrgTimeZone();
  const { data: items, isLoading } = useProposedEvidence();
  const confirmMutation = useBatchConfirmEvidence();
  const dismissMutation = useBatchDismissEvidence();
  const scanMutation = useScanEvidence();

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scanSummary, setScanSummary] = useState<EvidenceScanResult | null>(null);

  const allIds = items?.map((i) => i.id) ?? [];
  const allSelected = allIds.length > 0 && selected.size === allIds.length;

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  }

  function toggleItem(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleBatchConfirm() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    confirmMutation.mutate(ids, {
      onSuccess: () => setSelected(new Set()),
    });
  }

  function handleBatchDismiss() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    dismissMutation.mutate(ids, {
      onSuccess: () => setSelected(new Set()),
    });
  }

  function handleScan() {
    setScanSummary(null);
    scanMutation.mutate(undefined, {
      onSuccess: (data) => setScanSummary(data),
    });
  }

  const isMutating =
    confirmMutation.isPending || dismissMutation.isPending || scanMutation.isPending;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Proposed Evidence
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Evidence auto-harvested by the platform awaiting your review
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={scanMutation.isPending}
            onClick={handleScan}
          >
            <RefreshCw
              className={`mr-1.5 h-4 w-4 ${scanMutation.isPending ? 'animate-spin' : ''}`}
            />
            Scan for evidence
          </Button>
          {items && items.length > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1.5 text-sm">
              <Sparkles className="h-3.5 w-3.5" />
              {items.length} pending
            </Badge>
          )}
        </div>
      </div>

      {scanSummary && (
        <p className="text-sm text-gray-600">
          Scan complete: {scanSummary.scanned} records scanned, {scanSummary.proposed} new
          proposals, {scanSummary.already_linked} already linked.
        </p>
      )}

      {/* Batch actions bar */}
      {items && items.length > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Select all ({allIds.length})
            </label>

            <span className="text-sm text-gray-400">
              {selected.size} selected
            </span>

            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="default"
                disabled={selected.size === 0 || isMutating}
                onClick={handleBatchConfirm}
              >
                <Check className="mr-1.5 h-4 w-4" />
                Confirm{selected.size > 0 ? ` (${selected.size})` : ''}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={selected.size === 0 || isMutating}
                onClick={handleBatchDismiss}
              >
                <X className="mr-1.5 h-4 w-4" />
                Dismiss{selected.size > 0 ? ` (${selected.size})` : ''}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!items || items.length === 0) && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Inbox className="h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No proposed evidence
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              When the platform harvests evidence for your clauses it will appear here for review.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Evidence list */}
      {!isLoading && items && items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <Card
              key={item.id}
              className={`transition-colors ${selected.has(item.id) ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}
            >
              <CardContent className="flex items-center gap-4 py-3">
                <input
                  type="checkbox"
                  checked={selected.has(item.id)}
                  onChange={() => toggleItem(item.id)}
                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="truncate font-medium text-gray-900">
                      {item.title}
                    </span>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {item.evidence_type_display}
                    </Badge>
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>
                      Source: <span className="font-medium text-gray-700">{sourceLabel(item)}</span>
                    </span>
                    <span>
                      Clause:{' '}
                      <Link
                        to={`/compliance/clauses/${item.clause_status_id}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {item.clause_ref} — {item.clause_title}
                      </Link>
                    </span>
                    <span>Harvested: {formatDate(item.harvested_at, timeZone)}</span>
                  </div>
                </div>

                {/* Per-item quick actions */}
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                    disabled={isMutating}
                    onClick={() =>
                      confirmMutation.mutate([item.id], {
                        onSuccess: () =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            next.delete(item.id);
                            return next;
                          }),
                      })
                    }
                    title="Confirm"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    disabled={isMutating}
                    onClick={() =>
                      dismissMutation.mutate([item.id], {
                        onSuccess: () =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            next.delete(item.id);
                            return next;
                          }),
                      })
                    }
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Link to={`/compliance/clauses/${item.clause_status_id}`}>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                      title="View clause"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
